import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Merge, Loader2, Phone } from "lucide-react";

function DupGroup({ g, i, onMerged, bname }) {
  const [keep, setKeep] = useState(g.students[0].id);
  const [busy, setBusy] = useState(false);
  async function merge() {
    const k = g.students.find((s) => s.id === keep);
    const others = g.students.filter((s) => s.id !== keep);
    if (!window.confirm(`Keep ${k.name} (${k.admission_number}) and merge ${others.map((o) => o.admission_number).join(", ")} into it? Attendance, fees, marks and updates move to the kept record and the others are removed.`)) return;
    setBusy(true);
    try {
      const r = await api.post("/student-merge", { keep_id: keep, merge_ids: others.map((o) => o.id) });
      toast.success(`Merged ${r.data.merged} record(s) into ${k.name}`); onMerged();
    } catch (e) { toast.error(formatApiError(e)); } finally { setBusy(false); }
  }
  return (
    <div data-testid={`dup-group-${i}`} className="bg-white rounded-2xl border border-amber-200 p-4 space-y-2">
      <div className="flex items-center gap-2"><Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100">{g.reason}</Badge><span className="text-xs text-slate-500">Choose the record to keep</span></div>
      {g.students.map((s) => (
        <label key={s.id} className={`flex flex-wrap items-center gap-3 text-sm px-3 py-2 rounded-xl cursor-pointer border transition-colors ${keep === s.id ? "border-violet-400 bg-violet-50" : "border-slate-100 hover:bg-slate-50"}`}>
          <input type="radio" data-testid={`dup-keep-${s.id}`} checked={keep === s.id} onChange={() => setKeep(s.id)} className="accent-violet-600" />
          <span className="font-semibold flex-1 min-w-[160px]">{s.name}</span>
          <span className="font-mono text-xs text-slate-500">{s.admission_number}</span>
          <span className="w-40 truncate">{s.class_name} - {s.section}</span>
          <span className="w-48 truncate text-xs text-slate-500">{bname(s.branch_id)}</span>
          <span className="w-44 truncate text-slate-600 flex items-center gap-1"><Phone size={12} />{s.parent_phone || "—"} · {s.father_name || "—"}</span>
          {keep === s.id && <span className="text-xs font-semibold text-violet-700">Keep</span>}
        </label>
      ))}
      <div className="flex justify-end">
        <Button data-testid={`dup-merge-${i}`} size="sm" className="btn-brand" disabled={busy} onClick={merge}>
          {busy ? <Loader2 size={14} className="animate-spin mr-1" /> : <Merge size={14} className="mr-1" />} Merge into selected
        </Button>
      </div>
    </div>
  );
}

function MergeAllBar({ groups, branches, onDone }) {
  const [keepBranch, setKeepBranch] = useState("");
  const [progress, setProgress] = useState(null);
  const used = branches.filter((b) => groups.some((g) => g.students.some((s) => s.branch_id === b.id)));
  const plan = groups.map((g) => ({ g, keep: g.students.find((s) => s.branch_id === keepBranch) })).filter((x) => x.keep);
  async function run() {
    const bname = branches.find((b) => b.id === keepBranch)?.name;
    if (!window.confirm(`Merge ${plan.length} duplicate group(s), keeping the copy in ${bname}? Records from the other copies move to the kept student and the extra copies are removed.`)) return;
    let ok = 0, fail = 0;
    for (let i = 0; i < plan.length; i++) {
      setProgress(`${i + 1} / ${plan.length}`);
      const { g, keep } = plan[i];
      try { await api.post("/student-merge", { keep_id: keep.id, merge_ids: g.students.filter((s) => s.id !== keep.id).map((s) => s.id) }); ok++; }
      catch { fail++; }
    }
    setProgress(null);
    toast.success(`Merged ${ok} group(s)${fail ? `, ${fail} failed` : ""}`);
    onDone();
  }
  return (
    <div data-testid="merge-all-bar" className="bg-violet-50 border border-violet-100 rounded-2xl p-4 flex flex-wrap items-center gap-3">
      <span className="text-sm font-semibold text-violet-900">Merge all · keep the copy in</span>
      <Select value={keepBranch} onValueChange={setKeepBranch}>
        <SelectTrigger data-testid="merge-all-branch" className="w-64 bg-white"><SelectValue placeholder="Choose branch" /></SelectTrigger>
        <SelectContent>{used.map((b) => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}</SelectContent>
      </Select>
      {keepBranch && <span className="text-xs text-slate-500">{plan.length} of {groups.length} groups have a copy in this branch; others are left as they are.</span>}
      <span className="flex-1" />
      <Button data-testid="merge-all-btn" className="btn-brand" disabled={!plan.length || !!progress} onClick={run}>
        {progress ? <><Loader2 size={14} className="animate-spin mr-1" /> Merging {progress}</> : <><Merge size={14} className="mr-1" /> Merge all ({plan.length})</>}
      </Button>
    </div>
  );
}

export function DuplicatesPanel({ branchId, onChanged }) {
  const [groups, setGroups] = useState(null);
  const [branches, setBranches] = useState([]);
  useEffect(() => { api.get("/branches").then((r) => setBranches(r.data)).catch(() => {}); }, []);
  const bname = (id) => branches.find((b) => b.id === id)?.name || "Branch not found";
  const load = () => api.get("/student-duplicates", { params: { branch_id: branchId === "all" ? undefined : branchId } }).then((r) => setGroups(r.data)).catch((e) => toast.error(formatApiError(e)));
  useEffect(() => { load(); }, [branchId]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!groups) return <div className="py-16 flex justify-center"><Loader2 className="animate-spin text-violet-500" /></div>;
  if (!groups.length) return <p data-testid="dup-none" className="bg-white rounded-2xl p-6 text-sm text-slate-500">No possible duplicates found.</p>;
  return (
    <div data-testid="dup-panel" className="space-y-3">
      <p className="text-sm text-slate-600">{groups.length} possible duplicate group(s): same student name, or same mobile number and father name.</p>
      <MergeAllBar groups={groups} branches={branches} onDone={() => { load(); onChanged(); }} />
      {groups.map((g, i) => <DupGroup key={g.students.map((s) => s.id).join("")} g={g} i={i} bname={bname} onMerged={() => { load(); onChanged(); }} />)}
    </div>
  );
}
